import React, { useEffect, useState } from 'react';
import { ArrowLeft, Search, ChevronDown, Plus, Minus, LayoutGrid, SquareDot, HelpCircle, Check, Code, Webhook, Package, CalendarClock, Calendar, FileText, MessageCircle, AlertCircle, ThumbsUp, MessageSquare, MoreHorizontal, CheckCircle, XCircle, RefreshCw, X, Clock, Info, Send, Timer, GitBranch, Split, Variable, Sparkles, Zap, Layers, Play, Save, Settings, Trash } from 'lucide-react';
import { ActionDetailsPanel } from './ActionDetailsPanel';
import { ActionSelectionPanel } from './ActionSelectionPanel';
import { OutputDataStructure, OutputProperty } from './OutputDataStructure';
export const AutomationBuilder = ({
  onClose
}) => {
  // Get the workflow name from session storage to determine if we're editing a specific workflow
  const editingWorkflow = sessionStorage.getItem('editingWorkflow');
  // Initialize with action panel open if we're editing a workflow
  const [isActionPanelOpen, setIsActionPanelOpen] = useState(!!editingWorkflow);
  const [isActionSelectionPanelOpen, setIsActionSelectionPanelOpen] = useState(false);
  const [isTriggerSelectionPanelOpen, setIsTriggerSelectionPanelOpen] = useState(false);
  const [isEditTaskOpen, setIsEditTaskOpen] = useState(false);
  const [isNodeOptionsPanelOpen, setIsNodeOptionsPanelOpen] = useState(false);
  const [isCreateInstructionsModalOpen, setIsCreateInstructionsModalOpen] = useState(false);
  const [isEditInstructionsModalOpen, setIsEditInstructionsModalOpen] = useState(false);
  const [createInstructionsStep, setCreateInstructionsStep] = useState(1); // Track which step of the modal we're on
  const [editInstructionsStep, setEditInstructionsStep] = useState(2); // Edit starts at step 2 (content)
  const [isGeneratingInstructions, setIsGeneratingInstructions] = useState(false); // Track generation state
  const [showTestPanel, setShowTestPanel] = useState(false); // Track test panel visibility
  const [activeNodePosition, setActiveNodePosition] = useState({
    top: 0,
    left: 0
  });
  const [currentTask, setCurrentTask] = useState({
    name: 'Delegate to AI Custom Agent',
    automations: 1,
    approvals: 1,
    lastRun: 'Aug 29, 2025, 1:23:30 PM'
  });
  const [instructionsName, setInstructionsName] = useState('');
  const [instructionsPrompt, setInstructionsPrompt] = useState('');
  const [instructionsIntentDescription, setInstructionsIntentDescription] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [flowNodes, setFlowNodes] = useState([{
    type: 'trigger',
    title: 'Order placed',
    subtitle: 'Checkout & Orders',
    icon: <Package className="w-4 h-4 text-white" data-id="element-302" />,
    iconBgColor: 'bg-blue-700',
    details: {
      starts: 'Immediately',
      ends: 'When order is processed'
    }
  }, {
    type: 'action',
    title: 'Delegate to AI Custom Agent',
    subtitle: 'AI Custom Agent',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-303">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="white" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-304" />
        </svg>,
    iconBgColor: 'bg-teal-400',
    position: 'left'
  }, {
    type: 'action',
    title: 'Delegate to AI Custom Agent',
    subtitle: 'AI Custom Agent',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-305">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="white" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-306" />
        </svg>,
    iconBgColor: 'bg-teal-400',
    position: 'right'
  }]);
  // Instructions templates
  const instructionsTemplates = [{
    id: 1,
    name: 'Inventory Management',
    description: 'Monitor inventory levels and automatically reorder when stock is low',
    intentDescription: 'Analyze inventory data daily to identify items with stock below the minimum threshold. For each low-stock item, identify the supplier and generate a personalized restock order email. Include current stock levels and recommended reorder quantity. Send the emails to suppliers and track responses.'
  }, {
    id: 2,
    name: 'Customer Support Assistant',
    description: 'Respond to common customer inquiries and route complex issues',
    intentDescription: 'Monitor incoming customer support messages. Categorize them by topic and urgency. For common questions, generate helpful responses using our knowledge base. For complex issues, assign them to the appropriate department with relevant context. Follow up with customers if no response is received within 24 hours.'
  }, {
    id: 3,
    name: 'Social Media Content Creator',
    description: 'Generate engaging posts for multiple social platforms',
    intentDescription: 'Create weekly social media content based on our product catalog and marketing calendar. Generate platform-specific posts for Twitter, Instagram, and Facebook that highlight product features and promotions. Include relevant hashtags and suggest optimal posting times. Prepare a content calendar for review.'
  }, {
    id: 4,
    name: 'Review Response Manager',
    description: 'Respond to customer reviews with personalized messages',
    intentDescription: 'Monitor new reviews across platforms. For positive reviews, generate thank you messages that reference specific points mentioned by the customer. For negative reviews, draft apologetic responses that address the concerns and offer solutions or compensation. All responses should follow our brand voice guidelines.'
  }];
  const [outputProperties, setOutputProperties] = useState<OutputProperty[]>([]);
  const handleSelectTemplate = template => {
    setSelectedTemplate(template.id);
    setInstructionsIntentDescription(template.intentDescription);
    // Immediately move to next step when a template is selected
    setIsGeneratingInstructions(true);
    setTimeout(() => {
      setInstructionsName(template.name);
      setInstructionsPrompt(`Based on your request: "${template.intentDescription}"\n\n1. Analyze relevant data\n2. Generate appropriate responses\n3. Execute requested actions\n4. Provide detailed reporting`);
      setCreateInstructionsStep(2); // Move to step 2
      setIsGeneratingInstructions(false);
    }, 800);
  };
  const handleActionClick = () => {
    setIsActionPanelOpen(true);
    setIsActionSelectionPanelOpen(false);
    setIsTriggerSelectionPanelOpen(false);
    setShowTestPanel(false); // Reset test panel when opening action panel
  };
  const handleTriggerClick = () => {
    setIsTriggerSelectionPanelOpen(true);
    setIsActionPanelOpen(false);
    setIsActionSelectionPanelOpen(false);
    setShowTestPanel(false);
  };
  const handleCloseActionPanel = () => {
    setIsActionPanelOpen(false);
    setShowTestPanel(false); // Close test panel when closing action panel
  };
  const handleCloseTriggerPanel = () => {
    setIsTriggerSelectionPanelOpen(false);
  };
  const handleShowActionSelection = () => {
    setIsActionPanelOpen(false);
    setIsActionSelectionPanelOpen(true);
    setIsTriggerSelectionPanelOpen(false);
    setShowTestPanel(false); // Close test panel when showing action selection
  };
  const handleCloseActionSelection = () => {
    setIsActionSelectionPanelOpen(false);
  };
  const handleShowTestPanel = () => {
    setShowTestPanel(true);
  };
  const handleEditTask = () => {
    // Instead of opening the modal, we'll directly open the action details panel
    // which now has inline editing capabilities
    setIsActionPanelOpen(true);
    setIsActionSelectionPanelOpen(false);
    setIsTriggerSelectionPanelOpen(false);
  };
  const handleCloseEditTask = () => {
    setIsEditTaskOpen(false);
  };
  const handleAddNodeClick = e => {
    // Get position of the clicked button
    const rect = e.currentTarget.getBoundingClientRect();
    setActiveNodePosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX - 150 // Center the panel
    });
    setIsNodeOptionsPanelOpen(true);
  };
  const handleNodeOptionClick = option => {
    setIsNodeOptionsPanelOpen(false);
    if (option === 'action') {
      setIsActionPanelOpen(false); // Close the action panel first
      setIsActionSelectionPanelOpen(true);
    }
    // Other options would be handled here
  };
  const handleAgentClick = () => {
    setIsCreateInstructionsModalOpen(true);
    setCreateInstructionsStep(1); // Reset to step 1 when opening
    setInstructionsName(''); // Reset form fields
    setInstructionsPrompt('');
    setInstructionsIntentDescription('');
  };
  const handleCloseCreateInstructions = () => {
    setIsCreateInstructionsModalOpen(false);
    setCreateInstructionsStep(1); // Reset to step 1 when closing
    // Reset form fields
    setInstructionsName('');
    setInstructionsPrompt('');
    setInstructionsIntentDescription('');
  };
  const handleNextStep = () => {
    if (instructionsIntentDescription.trim()) {
      setIsGeneratingInstructions(true);
      // Simulate AI generating content based on intent description
      setTimeout(() => {
        // Use template name if a template was selected
        if (selectedTemplate) {
          const template = instructionsTemplates.find(t => t.id === selectedTemplate);
          setInstructionsName(template.name);
        } else {
          setInstructionsName(instructionsIntentDescription.includes('restock') ? 'Inventory Restock Manager' : instructionsIntentDescription.includes('email') ? 'Email Response Generator' : 'Custom AI Instructions');
        }
        setInstructionsPrompt(`Based on your request: "${instructionsIntentDescription}"\n\n1. Analyze relevant data\n2. Generate appropriate responses\n3. Execute requested actions\n4. Provide detailed reporting`);
        setCreateInstructionsStep(2); // Move to step 2
        setIsGeneratingInstructions(false);
      }, 800);
    }
  };
  const handlePreviousStep = () => {
    setCreateInstructionsStep(1); // Go back to step 1
  };
  const handleCreateInstructions = () => {
    // Create a new node with the instructions title
    if (instructionsName.trim()) {
      const newNode = {
        type: 'action',
        title: instructionsName,
        subtitle: 'AI Custom Agent',
        icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-307">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="white" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-308" />
          </svg>,
        iconBgColor: 'bg-teal-400'
      };
      // Add the new node to the flowchart
      setFlowNodes(prevNodes => {
        const newNodes = [...prevNodes];
        // Insert the new node before the last one if there are more than 1 nodes
        if (newNodes.length > 1) {
          return [...newNodes.slice(0, -1), newNode, newNodes[newNodes.length - 1]];
        }
        return [...newNodes, newNode];
      });
      // Close the modal
      setIsCreateInstructionsModalOpen(false);
      // Open the Action Details panel instead of closing all panels
      setIsActionPanelOpen(true);
      setIsActionSelectionPanelOpen(false);
      setIsTriggerSelectionPanelOpen(false);
      // Reset form fields
      setInstructionsName('');
      setInstructionsPrompt('');
      setInstructionsIntentDescription('');
    }
  };
  const handleGenerateWithAI = () => {
    // Simulate AI generating content based on intent description
    if (instructionsIntentDescription.trim()) {
      // This would be where you'd make an API call to an AI service
      // For now, we'll just set some placeholder content
      setTimeout(() => {
        setInstructionsName(instructionsIntentDescription.includes('restock') ? 'Inventory Restock Manager' : instructionsIntentDescription.includes('email') ? 'Email Response Generator' : 'Custom AI Instructions');
        setInstructionsPrompt(`Based on your request: "${instructionsIntentDescription}"\n\n1. Analyze relevant data\n2. Generate appropriate responses\n3. Execute requested actions\n4. Provide detailed reporting`);
      }, 500);
    }
  };
  const handleExit = () => {
    // Clear the editing workflow flag
    sessionStorage.removeItem('editingWorkflow');
    // Call the onClose callback from the parent
    if (typeof onClose === 'function') {
      onClose();
    }
  };
  const handleUpdateInstructions = () => {
    // Update the node with the edited instructions title
    // Update the existing node
    setFlowNodes(prevNodes => {
      const newNodes = [...prevNodes];
      // Find the action node and update it
      const actionNodeIndex = newNodes.findIndex(node => node.type === 'action');
      if (actionNodeIndex !== -1) {
        newNodes[actionNodeIndex] = {
          ...newNodes[actionNodeIndex]
        };
      }
      return newNodes;
    });
    // Close the modal
    setIsEditInstructionsModalOpen(false);
    // Open the Action Details panel
    setIsActionPanelOpen(true);
    setIsActionSelectionPanelOpen(false);
    setIsTriggerSelectionPanelOpen(false);
    // Reset form fields
    setInstructionsName('');
    setInstructionsPrompt('');
  };
  if (isEditTaskOpen) {
    return <EditTask task={currentTask} onClose={onClose} // This now goes back to the Dashboard directly
    isNewTask={false} data-id="element-309" />;
  }
  return <div className="fixed inset-0 bg-white z-50 flex flex-col" data-id="element-310">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white p-4 flex justify-between items-center" data-id="element-311">
        <div className="flex items-center" data-id="element-312">
          <button onClick={handleExit} className="mr-4 text-gray-500 hover:text-gray-700" data-id="element-313">
            <ArrowLeft className="w-5 h-5" data-id="element-314" />
          </button>
          <button className="mr-4 text-gray-700 font-medium" onClick={handleExit} data-id="element-315">
            Exit
          </button>
          <div className="h-4 border-r border-gray-300 mx-2" data-id="element-316"></div>
          <span className="text-gray-700 font-medium" data-id="element-317">
            Respond to Negative Reviews with Coupons
          </span>
        </div>
        <div className="flex items-center" data-id="element-318">
          <button className="px-4 py-2 border border-gray-200 rounded-md mr-2 text-sm font-medium" data-id="element-319">
            Save
          </button>
          <button className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm font-medium" data-id="element-320">
            Activate
          </button>
        </div>
      </div>
      {/* Main content */}
      <div className="flex flex-1 overflow-hidden" data-id="element-321">
        {/* Left sidebar for trigger selection or action details */}
        {isActionPanelOpen ? <ActionDetailsPanel onClose={handleCloseActionPanel} onShowActionSelection={handleShowActionSelection} onShowTestPanel={handleShowTestPanel} data-id="element-322" /> : isActionSelectionPanelOpen ? <ActionSelectionPanel onClose={handleCloseActionSelection} onAgentClick={handleAgentClick} data-id="element-323" /> : isTriggerSelectionPanelOpen ? <div className="w-[400px] border-r border-gray-200 bg-white flex flex-col h-full" data-id="element-324">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-325">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-326">
                Select a trigger
              </h2>
              <button onClick={handleCloseTriggerPanel} className="text-gray-500 hover:text-gray-700" data-id="element-327">
                <X className="w-5 h-5" data-id="element-328" />
              </button>
            </div>
            <div className="p-4 border-b border-gray-200" data-id="element-329">
              <p className="text-sm text-gray-600 mb-4" data-id="element-330">
                The trigger is what initiates the automation.
              </p>
              <div className="relative mb-4" data-id="element-331">
                <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" data-id="element-332" />
                <input type="text" placeholder="Search for triggers or apps" className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-full text-sm" data-id="element-333" />
              </div>
            </div>
            <div className="overflow-y-auto flex-1" data-id="element-334">
              <div className="p-4" data-id="element-335">
                <div className="mb-4" data-id="element-336">
                  <div className="flex justify-between items-center mb-2" data-id="element-337">
                    <h3 className="text-sm text-gray-500" data-id="element-338">Suggested (0)</h3>
                    <ChevronDown className="w-4 h-4 text-gray-500" data-id="element-339" />
                  </div>
                  <div className="border-t border-gray-100" data-id="element-340"></div>
                </div>
                <div className="mb-4" data-id="element-341">
                  <div className="flex justify-between items-center mb-2" data-id="element-342">
                    <h3 className="text-sm text-gray-500" data-id="element-343">Wix Automations</h3>
                    <span className="text-sm text-blue-500" data-id="element-344">Show All (9)</span>
                  </div>
                  <div className="border border-gray-200 rounded-md mb-2 hover:bg-gray-50 cursor-pointer" data-id="element-345">
                    <div className="p-3 flex items-center" data-id="element-346">
                      <div className="w-8 h-8 bg-gray-800 rounded flex items-center justify-center mr-3" data-id="element-347">
                        <CalendarClock className="w-4 h-4 text-white" data-id="element-348" />
                      </div>
                      <div data-id="element-349">
                        <div className="text-xs text-gray-500" data-id="element-350">
                          Wix Automations
                        </div>
                        <div className="text-sm font-medium" data-id="element-351">
                          Scheduled time
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-md mb-2 hover:bg-gray-50 cursor-pointer" data-id="element-352">
                    <div className="p-3 flex items-center" data-id="element-353">
                      <div className="w-8 h-8 bg-gray-800 rounded flex items-center justify-center mr-3" data-id="element-354">
                        <Code className="w-4 h-4 text-white" data-id="element-355" />
                      </div>
                      <div data-id="element-356">
                        <div className="text-xs text-gray-500" data-id="element-357">
                          Wix Automations
                        </div>
                        <div className="text-sm font-medium" data-id="element-358">
                          Velo code trigger
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-md mb-2 hover:bg-gray-50 cursor-pointer" data-id="element-359">
                    <div className="p-3 flex items-center" data-id="element-360">
                      <div className="w-8 h-8 bg-gray-800 rounded flex items-center justify-center mr-3" data-id="element-361">
                        <Webhook className="w-4 h-4 text-white" data-id="element-362" />
                      </div>
                      <div data-id="element-363">
                        <div className="text-xs text-gray-500" data-id="element-364">
                          Wix Automations
                        </div>
                        <div className="text-sm font-medium" data-id="element-365">
                          Webhook received
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-4" data-id="element-366">
                  <div className="flex justify-between items-center mb-2" data-id="element-367">
                    <h3 className="text-sm text-gray-500" data-id="element-368">Checkout & Orders</h3>
                  </div>
                  <div className="border border-gray-200 rounded-md mb-2 hover:bg-gray-50 cursor-pointer" data-id="element-369">
                    <div className="p-3 flex items-center" data-id="element-370">
                      <div className="w-8 h-8 bg-blue-700 rounded flex items-center justify-center mr-3" data-id="element-371">
                        <Package className="w-4 h-4 text-white" data-id="element-372" />
                      </div>
                      <div data-id="element-373">
                        <div className="text-xs text-gray-500" data-id="element-374">
                          Checkout & Orders
                        </div>
                        <div className="text-sm font-medium" data-id="element-375">Order placed</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-4" data-id="element-376">
                  <div className="flex justify-between items-center mb-2" data-id="element-377">
                    <h3 className="text-sm text-gray-500" data-id="element-378">Wix Bookings</h3>
                    <span className="text-sm text-blue-500" data-id="element-379">Show All (13)</span>
                  </div>
                  <div className="border border-gray-200 rounded-md mb-2 hover:bg-gray-50 cursor-pointer" data-id="element-380">
                    <div className="p-3 flex items-center" data-id="element-381">
                      <div className="w-8 h-8 bg-indigo-500 rounded flex items-center justify-center mr-3" data-id="element-382">
                        <Calendar className="w-4 h-4 text-white" data-id="element-383" />
                      </div>
                      <div data-id="element-384">
                        <div className="text-xs text-gray-500" data-id="element-385">
                          Wix Bookings
                        </div>
                        <div className="text-sm font-medium" data-id="element-386">
                          Session booked
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-md mb-2 hover:bg-gray-50 cursor-pointer" data-id="element-387">
                    <div className="p-3 flex items-center" data-id="element-388">
                      <div className="w-8 h-8 bg-indigo-500 rounded flex items-center justify-center mr-3" data-id="element-389">
                        <Calendar className="w-4 h-4 text-white" data-id="element-390" />
                      </div>
                      <div data-id="element-391">
                        <div className="text-xs text-gray-500" data-id="element-392">
                          Wix Bookings
                        </div>
                        <div className="text-sm font-medium" data-id="element-393">
                          Booking canceled
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div> : <div className="w-[400px] border-r border-gray-200 bg-white flex flex-col h-full" data-id="element-394">
            <div className="p-4 border-b border-gray-200" data-id="element-395">
              <div className="flex justify-between items-center mb-2" data-id="element-396">
                <h2 className="text-lg font-medium text-gray-800" data-id="element-397">
                  Select a trigger
                </h2>
                <div className="flex" data-id="element-398">
                  <button className="text-gray-500 hover:text-gray-700 mr-2" data-id="element-399">
                    <HelpCircle className="w-5 h-5" data-id="element-400" />
                  </button>
                  <button className="text-gray-500 hover:text-gray-700" data-id="element-401">
                    <ChevronDown className="w-5 h-5 transform rotate-45" data-id="element-402" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4" data-id="element-403">
                The trigger is what initiates the automation.
              </p>
              <div className="relative mb-4" data-id="element-404">
                <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" data-id="element-405" />
                <input type="text" placeholder="Search for triggers or apps" className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-full text-sm" data-id="element-406" />
              </div>
            </div>
            <div className="overflow-y-auto flex-1" data-id="element-407">
              <div className="p-4" data-id="element-408">
                <div className="mb-4" data-id="element-409">
                  <div className="flex justify-between items-center mb-2" data-id="element-410">
                    <h3 className="text-sm text-gray-500" data-id="element-411">Suggested (0)</h3>
                    <ChevronDown className="w-4 h-4 text-gray-500" data-id="element-412" />
                  </div>
                  <div className="border-t border-gray-100" data-id="element-413"></div>
                </div>
                <div className="mb-4" data-id="element-414">
                  <div className="flex justify-between items-center mb-2" data-id="element-415">
                    <h3 className="text-sm text-gray-500" data-id="element-416">Wix Automations</h3>
                    <span className="text-sm text-blue-500" data-id="element-417">Show All (9)</span>
                  </div>
                  <div className="border border-gray-200 rounded-md mb-2 hover:bg-gray-50 cursor-pointer" data-id="element-418">
                    <div className="p-3 flex items-center" data-id="element-419">
                      <div className="w-8 h-8 bg-gray-800 rounded flex items-center justify-center mr-3" data-id="element-420">
                        <CalendarClock className="w-4 h-4 text-white" data-id="element-421" />
                      </div>
                      <div data-id="element-422">
                        <div className="text-xs text-gray-500" data-id="element-423">
                          Wix Automations
                        </div>
                        <div className="text-sm font-medium" data-id="element-424">
                          Scheduled time
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-md mb-2 hover:bg-gray-50 cursor-pointer" data-id="element-425">
                    <div className="p-3 flex items-center" data-id="element-426">
                      <div className="w-8 h-8 bg-gray-800 rounded flex items-center justify-center mr-3" data-id="element-427">
                        <Code className="w-4 h-4 text-white" data-id="element-428" />
                      </div>
                      <div data-id="element-429">
                        <div className="text-xs text-gray-500" data-id="element-430">
                          Wix Automations
                        </div>
                        <div className="text-sm font-medium" data-id="element-431">
                          Velo code trigger
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-md mb-2 hover:bg-gray-50 cursor-pointer" data-id="element-432">
                    <div className="p-3 flex items-center" data-id="element-433">
                      <div className="w-8 h-8 bg-gray-800 rounded flex items-center justify-center mr-3" data-id="element-434">
                        <Webhook className="w-4 h-4 text-white" data-id="element-435" />
                      </div>
                      <div data-id="element-436">
                        <div className="text-xs text-gray-500" data-id="element-437">
                          Wix Automations
                        </div>
                        <div className="text-sm font-medium" data-id="element-438">
                          Webhook received
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-4" data-id="element-439">
                  <div className="flex justify-between items-center mb-2" data-id="element-440">
                    <h3 className="text-sm text-gray-500" data-id="element-441">Checkout & Orders</h3>
                  </div>
                  <div className="border border-gray-200 rounded-md mb-2 hover:bg-gray-50 cursor-pointer" data-id="element-442">
                    <div className="p-3 flex items-center" data-id="element-443">
                      <div className="w-8 h-8 bg-blue-700 rounded flex items-center justify-center mr-3" data-id="element-444">
                        <Package className="w-4 h-4 text-white" data-id="element-445" />
                      </div>
                      <div data-id="element-446">
                        <div className="text-xs text-gray-500" data-id="element-447">
                          Checkout & Orders
                        </div>
                        <div className="text-sm font-medium" data-id="element-448">Order placed</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-4" data-id="element-449">
                  <div className="flex justify-between items-center mb-2" data-id="element-450">
                    <h3 className="text-sm text-gray-500" data-id="element-451">Wix Bookings</h3>
                    <span className="text-sm text-blue-500" data-id="element-452">Show All (13)</span>
                  </div>
                  <div className="border border-gray-200 rounded-md mb-2 hover:bg-gray-50 cursor-pointer" data-id="element-453">
                    <div className="p-3 flex items-center" data-id="element-454">
                      <div className="w-8 h-8 bg-indigo-500 rounded flex items-center justify-center mr-3" data-id="element-455">
                        <Calendar className="w-4 h-4 text-white" data-id="element-456" />
                      </div>
                      <div data-id="element-457">
                        <div className="text-xs text-gray-500" data-id="element-458">
                          Wix Bookings
                        </div>
                        <div className="text-sm font-medium" data-id="element-459">
                          Session booked
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-md mb-2 hover:bg-gray-50 cursor-pointer" data-id="element-460">
                    <div className="p-3 flex items-center" data-id="element-461">
                      <div className="w-8 h-8 bg-indigo-500 rounded flex items-center justify-center mr-3" data-id="element-462">
                        <Calendar className="w-4 h-4 text-white" data-id="element-463" />
                      </div>
                      <div data-id="element-464">
                        <div className="text-xs text-gray-500" data-id="element-465">
                          Wix Bookings
                        </div>
                        <div className="text-sm font-medium" data-id="element-466">
                          Booking canceled
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>}
        {/* Main canvas */}
        <div className={`flex-1 bg-gray-50 overflow-auto relative ${showTestPanel ? 'w-[calc(100%-680px)]' : ''}`} data-id="element-467">
          <div className="min-h-full w-full" style={{
          backgroundImage: 'radial-gradient(circle, #e5e7eb 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} data-id="element-468">
            {/* Canvas controls */}
            <div className="absolute top-4 right-4 flex flex-col bg-white border border-gray-200 rounded-md shadow-sm" data-id="element-469">
              <button className="p-2 hover:bg-gray-50 border-b border-gray-200" data-id="element-470">
                <Plus className="w-4 h-4 text-gray-600" data-id="element-471" />
              </button>
              <button className="p-2 hover:bg-gray-50 border-b border-gray-200" data-id="element-472">
                <Minus className="w-4 h-4 text-gray-600" data-id="element-473" />
              </button>
              <button className="p-2 hover:bg-gray-50 border-b border-gray-200" data-id="element-474">
                <LayoutGrid className="w-4 h-4 text-gray-600" data-id="element-475" />
              </button>
              <button className="p-2 hover:bg-gray-50 border-b border-gray-200" data-id="element-476">
                <SquareDot className="w-4 h-4 text-gray-600" data-id="element-477" />
              </button>
              <button className="p-2 hover:bg-gray-50" data-id="element-478">
                <HelpCircle className="w-4 h-4 text-gray-600" data-id="element-479" />
              </button>
            </div>
            {/* Flow diagram */}
            <div className="flex flex-col items-center pt-32 pb-32" data-id="element-480">
              {/* Trigger Node */}
              <div className="relative" data-id="element-481">
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded" data-id="element-482">
                  TRIGGER
                </div>
                <div className="w-[350px] border border-gray-200 rounded-md bg-white shadow-sm cursor-pointer hover:bg-gray-50" onClick={handleTriggerClick} data-id="element-483">
                  <div className="p-4 flex items-start" data-id="element-484">
                    <div className="w-8 h-8 bg-blue-700 rounded flex items-center justify-center mr-3 flex-shrink-0" data-id="element-485">
                      <Package className="w-4 h-4 text-white" data-id="element-486" />
                    </div>
                    <div className="flex-1" data-id="element-487">
                      <div className="text-xs text-gray-500" data-id="element-488">
                        Checkout & Orders
                      </div>
                      <div className="text-sm font-medium mb-4" data-id="element-489">
                        Order placed
                      </div>
                      <div className="text-xs text-gray-600" data-id="element-490">
                        Starts: Immediately
                      </div>
                      <div className="text-xs text-gray-600" data-id="element-491">
                        Ends: When order is processed
                      </div>
                    </div>
                    <Check className="w-4 h-4 text-gray-600" data-id="element-492" />
                  </div>
                </div>
              </div>
              {/* Connector line */}
              <div className="w-px h-12 bg-gray-300" data-id="element-493"></div>
              {/* Add node button */}
              <div className="my-2" data-id="element-494">
                <button className="w-7 h-7 rounded-full border border-gray-300 bg-white flex items-center justify-center" onClick={handleAddNodeClick} data-id="element-495">
                  <Plus className="w-4 h-4 text-gray-600" data-id="element-496" />
                </button>
              </div>
              {/* Connector line */}
              <div className="w-px h-12 bg-gray-300" data-id="element-497"></div>
              {/* Parallel Nodes Container */}
              <div className="flex justify-center items-start gap-8 w-full" data-id="element-498">
                {/* Left Parallel Node */}
                <div className="flex flex-col items-center" data-id="element-499">
                  <div className="relative" data-id="element-500">
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-100 text-gray-800 text-xs px-2 py-0.5 rounded" data-id="element-501">
                      ACTION
                    </div>
                    <div className="w-[350px] border border-gray-200 rounded-md bg-white shadow-sm cursor-pointer hover:bg-gray-50" onClick={handleActionClick} data-id="element-502">
                      <div className="p-4 flex items-start" data-id="element-503">
                        <div className="w-8 h-8 bg-teal-400 rounded flex items-center justify-center mr-3 flex-shrink-0" data-id="element-504">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-505">
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="white" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-506" />
                          </svg>
                        </div>
                        <div className="flex-1" data-id="element-507">
                          <div className="text-xs text-gray-500" data-id="element-508">
                            AI Custom Agent
                          </div>
                          <div className="text-sm font-medium mb-4" data-id="element-509">
                            Delegate to AI Custom Agent
                          </div>
                        </div>
                        <Check className="w-4 h-4 text-gray-600" data-id="element-510" />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Right Parallel Node */}
                <div className="flex flex-col items-center" data-id="element-511">
                  <div className="relative" data-id="element-512">
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-100 text-gray-800 text-xs px-2 py-0.5 rounded" data-id="element-513">
                      ACTION
                    </div>
                    <div className="w-[350px] border border-gray-200 rounded-md bg-white shadow-sm cursor-pointer hover:bg-gray-50" onClick={handleActionClick} data-id="element-514">
                      <div className="p-4 flex items-start" data-id="element-515">
                        <div className="w-8 h-8 bg-teal-400 rounded flex items-center justify-center mr-3 flex-shrink-0" data-id="element-516">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-517">
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="white" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-518" />
                          </svg>
                        </div>
                        <div className="flex-1" data-id="element-519">
                          <div className="text-xs text-gray-500" data-id="element-520">
                            AI Custom Agent
                          </div>
                          <div className="text-sm font-medium mb-4" data-id="element-521">
                            Delegate to AI Custom Agent
                          </div>
                        </div>
                        <Check className="w-4 h-4 text-gray-600" data-id="element-522" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Connector line */}
              <div className="w-px h-12 bg-gray-300 mt-12" data-id="element-523"></div>
              {/* Add node button */}
              <div className="my-2" data-id="element-524">
                <button className="w-7 h-7 rounded-full border border-gray-300 bg-white flex items-center justify-center" onClick={handleAddNodeClick} data-id="element-525">
                  <Plus className="w-4 h-4 text-gray-600" data-id="element-526" />
                </button>
              </div>
              {/* Connector line */}
              <div className="w-px h-12 bg-gray-300" data-id="element-527"></div>
              {/* END node */}
              <div data-id="element-528">
                <div className="px-2 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded" data-id="element-529">
                  END
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Test Panel */}
        {showTestPanel && <div className="w-[280px] border-l border-gray-200 bg-white flex flex-col h-full overflow-hidden" data-id="element-530">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-531">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-532">Test agent</h2>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowTestPanel(false)} data-id="element-533">
                <RefreshCw className="w-4 h-4" data-id="element-534" />
              </button>
            </div>
            <div className="p-4 border-b border-gray-100" data-id="element-535">
              <div className="flex items-start" data-id="element-536">
                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-2" data-id="element-537">
                  <span className="text-xs text-gray-700" data-id="element-538">A</span>
                </div>
                <div data-id="element-539">
                  <div className="text-sm font-medium text-gray-800" data-id="element-540">
                    Albert
                  </div>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-541">
                    Let's start testing!
                    <br data-id="element-542" />
                    First, help me out – perform the trigger action and once
                    you're done, I will proceed with the prompt.
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 border-b border-gray-100" data-id="element-543">
              <div className="font-medium text-sm text-gray-800 mb-2" data-id="element-544">
                Perform trigger action
              </div>
              <div className="flex items-start mb-3" data-id="element-545">
                <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center mr-3 flex-shrink-0" data-id="element-546">
                  <Package className="w-4 h-4 text-blue-600" data-id="element-547" />
                </div>
                <div data-id="element-548">
                  <div className="text-sm font-medium text-gray-800" data-id="element-549">
                    Checkout & Orders
                  </div>
                  <div className="text-xs text-gray-600" data-id="element-550">Order placed</div>
                </div>
              </div>
              <div className="bg-green-50 p-3 rounded-md border border-green-100 flex items-center" data-id="element-551">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-552" />
                <span className="text-sm text-green-800" data-id="element-553">Agent triggered.</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto" data-id="element-554">
              <div className="p-4 border-b border-gray-100" data-id="element-555">
                <div className="flex items-center justify-between mb-1" data-id="element-556">
                  <div className="flex items-center" data-id="element-557">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-558" />
                    <span className="text-sm font-medium text-gray-800" data-id="element-559">
                      Searching inventory data
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 ml-6" data-id="element-560">
                  Current inventory information retrieved, providing insights
                  for restock orders.
                </p>
              </div>
              <div className="p-4 border-b border-gray-100" data-id="element-561">
                <div className="flex items-center justify-between mb-1" data-id="element-562">
                  <div className="flex items-center" data-id="element-563">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-564" />
                    <span className="text-sm font-medium text-gray-800" data-id="element-565">
                      Reading full documentation
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 ml-6" data-id="element-566">
                  Successfully retrieved the method for querying inventory
                  items, including details on filtering, sorting, and
                  pagination.
                </p>
              </div>
              <div className="p-4 border-b border-gray-100" data-id="element-567">
                <div className="flex items-center justify-between mb-1" data-id="element-568">
                  <div className="flex items-center" data-id="element-569">
                    <XCircle className="w-4 h-4 text-red-500 mr-2" data-id="element-570" />
                    <span className="text-sm font-medium text-gray-800" data-id="element-571">
                      Querying inventory items
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 ml-6" data-id="element-572">
                  Failed to query inventory items due to an incorrect catalog
                  version for the API call.
                </p>
              </div>
              <div className="p-4 border-b border-gray-100" data-id="element-573">
                <div className="flex items-center justify-between mb-1" data-id="element-574">
                  <div className="flex items-center" data-id="element-575">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-576" />
                    <span className="text-sm font-medium text-gray-800" data-id="element-577">
                      Searching Wix REST documentation
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 ml-6" data-id="element-578">
                  Successfully retrieved documentation on inventory query use
                  cases and API details for Catalog V1.
                </p>
              </div>
              <div className="p-4 border-b border-gray-100" data-id="element-579">
                <div className="flex items-center justify-between mb-1" data-id="element-580">
                  <div className="flex items-center" data-id="element-581">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-582" />
                    <span className="text-sm font-medium text-gray-800" data-id="element-583">
                      Getting inventory API documentation
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 ml-6" data-id="element-584">
                  The API schema for querying products was successfully
                  retrieved, detailing methods, parameters, and response types.
                </p>
              </div>
              <div className="p-4 border-b border-gray-100" data-id="element-585">
                <div className="flex items-center justify-between mb-1" data-id="element-586">
                  <div className="flex items-center" data-id="element-587">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-588" />
                    <span className="text-sm font-medium text-gray-800" data-id="element-589">
                      Querying products
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 ml-6" data-id="element-590">
                  Detailed product information and stock data retrieved
                  successfully, including 14 products with their respective
                  prices and availability.
                </p>
              </div>
              <div className="p-4 flex justify-center" data-id="element-591">
                <button className="flex items-center text-sm text-blue-500" data-id="element-592">
                  <RefreshCw className="w-4 h-4 mr-1" data-id="element-593" />
                  View Details
                </button>
              </div>
            </div>
          </div>}
      </div>
      {/* Node Options Panel */}
      {isNodeOptionsPanelOpen && <div className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden" style={{
      top: activeNodePosition.top + 'px',
      left: activeNodePosition.left + 'px'
    }} data-id="element-594">
          <div className="flex divide-x divide-gray-200" data-id="element-595">
            <button className="p-4 flex flex-col items-center justify-center hover:bg-gray-50" onClick={() => handleNodeOptionClick('action')} data-id="element-596">
              <Send className="w-6 h-6 text-gray-700 mb-2" data-id="element-597" />
              <span className="text-sm text-gray-700" data-id="element-598">Action</span>
            </button>
            <button className="p-4 flex flex-col items-center justify-center hover:bg-gray-50" onClick={() => handleNodeOptionClick('delay')} data-id="element-599">
              <Timer className="w-6 h-6 text-gray-700 mb-2" data-id="element-600" />
              <span className="text-sm text-gray-700" data-id="element-601">Delay</span>
            </button>
            <button className="p-4 flex flex-col items-center justify-center hover:bg-gray-50" onClick={() => handleNodeOptionClick('condition')} data-id="element-602">
              <GitBranch className="w-6 h-6 text-gray-700 mb-2" data-id="element-603" />
              <span className="text-sm text-gray-700" data-id="element-604">Condition</span>
            </button>
            <button className="p-4 flex flex-col items-center justify-center hover:bg-gray-50" onClick={() => handleNodeOptionClick('split')} data-id="element-605">
              <Split className="w-6 h-6 text-gray-700 mb-2" data-id="element-606" />
              <span className="text-sm text-gray-700" data-id="element-607">Split</span>
            </button>
            <button className="p-4 flex flex-col items-center justify-center hover:bg-gray-50" onClick={() => handleNodeOptionClick('variable')} data-id="element-608">
              <Variable className="w-6 h-6 text-gray-700 mb-2" data-id="element-609" />
              <span className="text-sm text-gray-700" data-id="element-610">Variable</span>
            </button>
          </div>
        </div>}
      {/* Create Instructions Modal */}
      {isCreateInstructionsModalOpen && <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50" data-id="element-611">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl" data-id="element-612">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between" data-id="element-613">
              <h2 className="text-xl font-medium text-gray-800" data-id="element-614">
                Create instructions for AI Custom Agent
              </h2>
              <button onClick={handleCloseCreateInstructions} className="text-gray-500 hover:text-gray-700" data-id="element-615">
                <X className="w-5 h-5" data-id="element-616" />
              </button>
            </div>
            {createInstructionsStep === 1 /* Step 1: Intent Description */ ? <div className="p-6" data-id="element-617">
                {/* Templates section */}
                <div className="mb-6" data-id="element-618">
                  <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-619">
                    Choose a template or create your own
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-id="element-620">
                    {instructionsTemplates.map(template => <div key={template.id} className={`border ${selectedTemplate === template.id ? 'border-gray-800' : 'border-gray-200'} rounded-md p-3 cursor-pointer hover:bg-gray-50`} onClick={() => handleSelectTemplate(template)} data-id="element-621">
                        <div className="text-sm font-medium text-gray-800 mb-1" data-id="element-622">
                          {template.name}
                        </div>
                        <div className="text-xs text-gray-600" data-id="element-623">
                          {template.description}
                        </div>
                      </div>)}
                  </div>
                </div>
                <div className="mb-6" data-id="element-624">
                  <div className="flex items-center mb-2" data-id="element-625">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-626">
                      What would you like the AI to do?
                    </label>
                    <Info className="w-4 h-4 text-gray-400 ml-1" data-id="element-627" />
                  </div>
                  <textarea className="w-full p-3 border border-gray-200 rounded-md h-40 resize-none" placeholder="Describe what you want this skill to do in detail. For example: 'Monitor our inventory and automatically send emails to suppliers when items are running low.'" value={instructionsIntentDescription} onChange={e => setInstructionsIntentDescription(e.target.value)} data-id="element-628"></textarea>
                  <div className="mt-2 text-xs text-gray-500" data-id="element-629">
                    AI can make mistakes, so you'll be able to review and edit
                    the generated instructions in the next step.
                  </div>
                </div>
              </div> : <div className="p-6" data-id="element-630">
                <div className="mb-6" data-id="element-631">
                  <label className="block text-sm text-gray-600 mb-2" data-id="element-632">
                    Title
                  </label>
                  <input type="text" className="w-full p-2 border border-gray-200 rounded-md" placeholder="Give your instructions a title" value={instructionsName} onChange={e => setInstructionsName(e.target.value)} data-id="element-633" />
                </div>
                <div className="mb-6" data-id="element-634">
                  <div className="flex items-center mb-2" data-id="element-635">
                    <label className="block text-sm text-gray-600" data-id="element-636">
                      Instructions
                    </label>
                    <Info className="w-4 h-4 text-gray-400 ml-1" data-id="element-637" />
                  </div>
                  <div className="border border-gray-200 rounded-md p-4 bg-gray-50" data-id="element-638">
                    <div className="text-sm text-gray-600 mb-3" data-id="element-639">
                      Based on your request: "
                      {instructionsIntentDescription.substring(0, 100)}
                      {instructionsIntentDescription.length > 100 ? '...' : ''}"
                    </div>
                    <ol className="list-decimal pl-5 space-y-3" data-id="element-640">
                      <li className="text-sm text-gray-700" data-id="element-641">
                        Analyze relevant data
                      </li>
                      <li className="text-sm text-gray-700" data-id="element-642">
                        Generate appropriate responses
                      </li>
                      <li className="text-sm text-gray-700" data-id="element-643">
                        Execute requested actions
                      </li>
                      <li className="text-sm text-gray-700" data-id="element-644">
                        Provide detailed reporting
                      </li>
                    </ol>
                  </div>
                  <div className="mt-2 text-xs text-gray-500" data-id="element-645">
                    AI can make mistakes, so double-check that the results are
                    accurate before using them.
                  </div>
                </div>
                <div className="flex items-center" data-id="element-646">
                  <div className="w-10 h-5 bg-gray-300 rounded-full relative mr-3" data-id="element-647">
                    <div className="absolute left-1 top-1 w-3 h-3 bg-white rounded-full" data-id="element-648"></div>
                  </div>
                  <div data-id="element-649">
                    <div className="text-sm font-medium text-gray-700" data-id="element-650">
                      Require approvals
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-651">
                      Review and confirm agent actions before it sends emails or
                      makes changes to your site.
                    </div>
                  </div>
                </div>
              </div>}
            <div className="p-4 border-t border-gray-200 flex justify-end" data-id="element-652">
              {createInstructionsStep === 1 ? <>
                  <button onClick={handleCloseCreateInstructions} className="px-4 py-2 border border-gray-200 rounded-md mr-2 text-sm font-medium" data-id="element-653">
                    Cancel
                  </button>
                  <button className={`px-4 py-2 ${isGeneratingInstructions ? 'bg-gray-400' : 'bg-blue-500'} text-white rounded-md text-sm font-medium flex items-center`} onClick={handleNextStep} disabled={!instructionsIntentDescription.trim() || isGeneratingInstructions} data-id="element-654">
                    {isGeneratingInstructions ? <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" data-id="element-655" />
                        Generating...
                      </> : <>
                        <Sparkles className="w-4 h-4 mr-2" data-id="element-656" />
                        Generate with AI
                      </>}
                  </button>
                </> : <>
                  <button onClick={handlePreviousStep} className="px-4 py-2 border border-gray-200 rounded-md mr-auto text-sm font-medium" data-id="element-657">
                    Back
                  </button>
                  <button onClick={handleCloseCreateInstructions} className="px-4 py-2 border border-gray-200 rounded-md mr-2 text-sm font-medium" data-id="element-658">
                    Cancel
                  </button>
                  <button className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium" onClick={handleCreateInstructions} disabled={!instructionsName.trim()} data-id="element-659">
                    Create Instructions
                  </button>
                </>}
            </div>
          </div>
        </div>}
    </div>;
};
// Import the EditTask component from Dashboard.tsx
const EditTask = ({
  task,
  onClose,
  isNewTask = false
}) => {
  const [showTestModal, setShowTestModal] = useState(false);
  const [showTestLog, setShowTestLog] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  // Sample automation data - just one for new skills
  const automations = isNewTask ? [{
    id: 1,
    name: 'Scheduled Agent Runner',
    description: 'Scheduled time → Delegate to AI Custom Agent',
    active: true
  }] : [{
    id: 1,
    name: 'Inventory Restock Notifier',
    description: 'Scheduled time → Delegate to AI Custom Agent',
    active: true
  }];
  const handleRunTest = () => {
    setShowTestModal(false);
    setShowWarningModal(false);
    setShowTestLog(true);
  };
  const handleCloseTestLog = () => {
    setShowTestLog(false);
  };
  const handleRunClick = () => {
    if (isNewTask) {
      // For new skills, show warning directly (skip automation selection)
      setShowWarningModal(true);
    } else {
      // For existing skills, show automation selection modal
      setShowTestModal(true);
    }
  };
  // New handler for when an automation is selected
  const handleSelectAutomation = () => {
    setShowTestModal(false); // Close the selection modal
    setShowWarningModal(true); // Show the warning modal
  };
  return <div className="max-w-full" data-id="element-660">
      {/* Header with buttons moved here */}
      <div className="flex items-center justify-between mb-6" data-id="element-661">
        <div className="flex items-center" data-id="element-662">
          <button onClick={onClose} className="mr-3 text-gray-500 hover:text-gray-700" data-id="element-663">
            <ArrowLeft className="w-5 h-5" data-id="element-664" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800" data-id="element-665">
            {isNewTask ? 'New Instructions' : 'Edit Instructions'}
          </h1>
        </div>
        <div className="flex items-center" data-id="element-666">
          <button onClick={onClose} className="px-6 py-2 border border-gray-200 rounded-md mr-2 text-sm font-medium" data-id="element-667">
            Cancel
          </button>
          <button className="px-6 py-2 bg-blue-500 text-white rounded-md text-sm font-medium" data-id="element-668">
            Save
          </button>
        </div>
      </div>
      <div className="flex gap-6" data-id="element-669">
        {/* Left column - Instructions definition */}
        <div className="flex-1" data-id="element-670">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6" data-id="element-671">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-672">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-673">
                Define instructions
              </h2>
            </div>
            <div className="p-6" data-id="element-674">
              <div className="mb-6" data-id="element-675">
                <label className="block text-sm text-gray-600 mb-2" data-id="element-676">
                  Label
                </label>
                <input type="text" className="w-full p-2 border border-gray-200 rounded-md" defaultValue={task?.name || 'New Instructions'} data-id="element-677" />
              </div>
              <div className="mb-6" data-id="element-678">
                <div className="flex items-center mb-2" data-id="element-679">
                  <label className="block text-sm text-gray-600" data-id="element-680">Prompt</label>
                  <Info className="w-4 h-4 text-gray-400 ml-1" data-id="element-681" />
                </div>
                <div className="border border-gray-200 rounded-md p-4" data-id="element-682">
                  <ol className="list-decimal pl-5 space-y-3" data-id="element-683">
                    <li className="text-sm text-gray-700" data-id="element-684">
                      Analyze the inventory data to identify any items with
                      stock levels below 20 units.
                    </li>
                    <li className="text-sm text-gray-700" data-id="element-685">
                      For each item identified, determine the corresponding
                      supplier responsible for restocking that item.
                    </li>
                    <li className="text-sm text-gray-700" data-id="element-686">
                      Draft a personalized restock order email for each
                      supplier, including the following details:
                      <ul className="list-disc pl-8 mt-2 space-y-2" data-id="element-687">
                        <li className="text-sm text-gray-700" data-id="element-688">
                          The item(s) that need restocking.
                        </li>
                        <li className="text-sm text-gray-700" data-id="element-689">
                          The current stock levels and the recommended restock
                          quantity.
                        </li>
                        <li className="text-sm text-gray-700" data-id="element-690">
                          A polite request to confirm the order and provide an
                          estimated delivery date.
                        </li>
                      </ul>
                    </li>
                    <li className="text-sm text-gray-700" data-id="element-691">
                      Send the email to the respective supplier.
                    </li>
                  </ol>
                </div>
                <div className="mt-2 text-xs text-gray-500" data-id="element-692">
                  AI can make mistakes, so double-check that the results are
                  accurate before using them.
                </div>
              </div>
              <div className="flex items-center" data-id="element-693">
                <div className="w-10 h-5 bg-blue-500 rounded-full relative mr-3 opacity-75 cursor-not-allowed" data-id="element-694">
                  <div className="absolute right-1 top-1 w-3 h-3 bg-white rounded-full" data-id="element-695"></div>
                </div>
                <div data-id="element-696">
                  <div className="text-sm font-medium text-gray-700" data-id="element-697">
                    Require approvals
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-698">
                    Review and confirm agent actions before it sends emails or
                    makes changes to your site.
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-699">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center" data-id="element-700">
              <h2 className="text-lg font-medium text-gray-800" data-id="element-701">
                Connected automations
              </h2>
              <Info className="w-4 h-4 text-gray-400 ml-1" data-id="element-702" />
            </div>
            <div className="p-6" data-id="element-703">
              {/* List of automations */}
              <div className="border border-gray-200 rounded-md mb-4 divide-y divide-gray-200" data-id="element-704">
                <div className="p-4 text-sm text-gray-500 bg-gray-50" data-id="element-705">
                  Agent will execute in following automations
                </div>
                {automations.map(automation => <div key={automation.id} className="p-4 flex items-center justify-between" data-id="element-706">
                    <div className="flex items-center" data-id="element-707">
                      <div className="w-10 h-5 bg-blue-500 rounded-full relative mr-3" data-id="element-708">
                        <div className="absolute right-1 top-1 w-3 h-3 bg-white rounded-full" data-id="element-709"></div>
                      </div>
                      <div data-id="element-710">
                        <div className="text-sm font-medium text-gray-700" data-id="element-711">
                          {automation.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-712">
                          {automation.description}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-713">
                      <button className="text-gray-500 hover:text-gray-700 mr-3" data-id="element-714">
                        <MoreHorizontal className="w-5 h-5" data-id="element-715" />
                      </button>
                      <button className="px-3 py-1 border border-gray-200 rounded-md text-sm font-medium text-gray-700 mr-2" onClick={() => setShowWarningModal(true)} data-id="element-716">
                        Run
                      </button>
                      <button className="px-3 py-1 border border-gray-200 rounded-md text-sm font-medium text-blue-500" data-id="element-717">
                        Edit
                      </button>
                    </div>
                  </div>)}
              </div>
              <button className="w-full py-2 border border-gray-200 rounded-md text-sm font-medium flex items-center justify-center" data-id="element-718">
                <Plus className="w-4 h-4 mr-1" data-id="element-719" />
                Create automation
              </button>
            </div>
          </div>
        </div>
        {/* Right column - Test agent */}
        <div className="w-80" data-id="element-720">
          {!showTestLog ? <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-721">
              <div className="px-6 py-4 border-b border-gray-200" data-id="element-722">
                <h2 className="text-lg font-medium text-gray-800" data-id="element-723">
                  Test agent
                </h2>
              </div>
              <div className="p-6 flex flex-col items-center" data-id="element-724">
                <div className="w-20 h-20 mb-4" data-id="element-725">
                  <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-726">
                    <rect width="80" height="80" rx="4" fill="#E5F5FF" data-id="element-727" />
                    <rect x="22" y="18" width="36" height="44" rx="2" fill="white" stroke="#20BDFF" strokeWidth="2" data-id="element-728" />
                    <rect x="28" y="26" width="24" height="4" rx="1" fill="#20BDFF" data-id="element-729" />
                    <rect x="28" y="34" width="24" height="4" rx="1" fill="#20BDFF" data-id="element-730" />
                    <rect x="28" y="42" width="24" height="4" rx="1" fill="#20BDFF" data-id="element-731" />
                    <rect x="28" y="50" width="12" height="4" rx="1" fill="#20BDFF" data-id="element-732" />
                  </svg>
                </div>
                <div className="text-sm font-medium text-gray-700 mb-1" data-id="element-733">
                  Run a test
                </div>
                <div className="text-xs text-gray-500 text-center mb-6" data-id="element-734">
                  See how the agent follows the prompt and refine if needed.
                </div>
                <button className="w-full py-2 bg-blue-500 text-white rounded-md font-medium" onClick={handleRunClick} data-id="element-735">
                  Run Test
                </button>
              </div>
            </div> : <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-736">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-737">
                <h2 className="text-lg font-medium text-gray-800" data-id="element-738">
                  Test agent
                </h2>
                <button className="text-gray-500 hover:text-gray-700" onClick={handleCloseTestLog} data-id="element-739">
                  <RefreshCw className="w-4 h-4" data-id="element-740" />
                </button>
              </div>
              <div className="p-4 border-b border-gray-100" data-id="element-741">
                <div className="flex items-start" data-id="element-742">
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-2" data-id="element-743">
                    <span className="text-xs text-gray-700" data-id="element-744">A</span>
                  </div>
                  <div data-id="element-745">
                    <div className="text-sm font-medium text-gray-800" data-id="element-746">
                      Albert
                    </div>
                    <p className="text-sm text-gray-600 mt-1" data-id="element-747">
                      Let's start testing!
                      <br data-id="element-748" />
                      First, help me out – perform the trigger action and once
                      you're done, I will proceed with the prompt.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-4 border-b border-gray-100" data-id="element-749">
                <div className="font-medium text-sm text-gray-800 mb-2" data-id="element-750">
                  Perform trigger action
                </div>
                <div className="flex items-start mb-3" data-id="element-751">
                  <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center mr-3 flex-shrink-0" data-id="element-752">
                    <Package className="w-4 h-4 text-blue-600" data-id="element-753" />
                  </div>
                  <div data-id="element-754">
                    <div className="text-sm font-medium text-gray-800" data-id="element-755">
                      Checkout & Orders
                    </div>
                    <div className="text-xs text-gray-600" data-id="element-756">Order placed</div>
                  </div>
                </div>
                <div className="bg-green-50 p-3 rounded-md border border-green-100 flex items-center" data-id="element-757">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-758" />
                  <span className="text-sm text-green-800" data-id="element-759">
                    Agent triggered.
                  </span>
                </div>
              </div>
              <div className="p-4 border-b border-gray-100" data-id="element-760">
                <div className="flex items-center justify-between mb-1" data-id="element-761">
                  <div className="flex items-center" data-id="element-762">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-763" />
                    <span className="text-sm font-medium text-gray-800" data-id="element-764">
                      Searching inventory data
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 ml-6" data-id="element-765">
                  Current inventory information retrieved, providing insights
                  for restock orders.
                </p>
              </div>
              <div className="p-4 border-b border-gray-100" data-id="element-766">
                <div className="flex items-center justify-between mb-1" data-id="element-767">
                  <div className="flex items-center" data-id="element-768">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-769" />
                    <span className="text-sm font-medium text-gray-800" data-id="element-770">
                      Reading full documentation
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 ml-6" data-id="element-771">
                  Successfully retrieved the method for querying inventory
                  items, including details on filtering, sorting, and
                  pagination.
                </p>
              </div>
              <div className="p-4 border-b border-gray-100" data-id="element-772">
                <div className="flex items-center justify-between mb-1" data-id="element-773">
                  <div className="flex items-center" data-id="element-774">
                    <XCircle className="w-4 h-4 text-red-500 mr-2" data-id="element-775" />
                    <span className="text-sm font-medium text-gray-800" data-id="element-776">
                      Querying inventory items
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 ml-6" data-id="element-777">
                  Failed to query inventory items due to an incorrect catalog
                  version for the API call.
                </p>
              </div>
              <div className="p-4 border-b border-gray-100" data-id="element-778">
                <div className="flex items-center justify-between mb-1" data-id="element-779">
                  <div className="flex items-center" data-id="element-780">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-781" />
                    <span className="text-sm font-medium text-gray-800" data-id="element-782">
                      Searching Wix REST documentation
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 ml-6" data-id="element-783">
                  Successfully retrieved documentation on inventory query use
                  cases and API details for Catalog V1.
                </p>
              </div>
              <div className="p-4 border-b border-gray-100" data-id="element-784">
                <div className="flex items-center justify-between mb-1" data-id="element-785">
                  <div className="flex items-center" data-id="element-786">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-787" />
                    <span className="text-sm font-medium text-gray-800" data-id="element-788">
                      Getting inventory API documentation
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 ml-6" data-id="element-789">
                  The API schema for querying products was successfully
                  retrieved, detailing methods, parameters, and response types.
                </p>
              </div>
              <div className="p-4 border-b border-gray-100" data-id="element-790">
                <div className="flex items-center justify-between mb-1" data-id="element-791">
                  <div className="flex items-center" data-id="element-792">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" data-id="element-793" />
                    <span className="text-sm font-medium text-gray-800" data-id="element-794">
                      Querying products
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 ml-6" data-id="element-795">
                  Detailed product information and stock data retrieved
                  successfully, including 14 products with their respective
                  prices and availability.
                </p>
              </div>
              <div className="p-4 flex justify-center" data-id="element-796">
                <button className="flex items-center text-sm text-blue-500" data-id="element-797">
                  <RefreshCw className="w-4 h-4 mr-1" data-id="element-798" />
                  View Details
                </button>
              </div>
            </div>}
        </div>
      </div>
      {/* Test Automation Modal */}
      {showTestModal && <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50" data-id="element-799">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md" data-id="element-800">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-801">
              <h3 className="text-lg font-medium text-gray-800" data-id="element-802">
                Select automation to test
              </h3>
              <button onClick={() => setShowTestModal(false)} className="text-gray-500 hover:text-gray-700" data-id="element-803">
                <X className="w-5 h-5" data-id="element-804" />
              </button>
            </div>
            <div className="p-4" data-id="element-805">
              <div className="space-y-3" data-id="element-806">
                {automations.map(automation => <div key={automation.id} className="p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer flex items-center" onClick={handleSelectAutomation} data-id="element-807">
                    <Clock className="w-5 h-5 text-gray-500 mr-3" data-id="element-808" />
                    <div data-id="element-809">
                      <div className="text-sm font-medium text-gray-700" data-id="element-810">
                        {automation.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-811">
                        {automation.description}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end" data-id="element-812">
              <button onClick={() => setShowTestModal(false)} className="px-4 py-2 border border-gray-200 rounded-md mr-2 text-sm font-medium" data-id="element-813">
                Cancel
              </button>
            </div>
          </div>
        </div>}
      {/* Warning Modal */}
      {showWarningModal && <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50" data-id="element-814">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md" data-id="element-815">
            <div className="p-4 border-b border-gray-200 flex items-center" data-id="element-816">
              <AlertCircle className="w-5 h-5 text-yellow-500 mr-2" data-id="element-817" />
              <h3 className="text-lg font-medium text-gray-800" data-id="element-818">
                Automation Warning
              </h3>
            </div>
            <div className="p-4" data-id="element-819">
              <p className="text-sm text-gray-600 mb-2" data-id="element-820">
                This test will execute the actual automation. All actions
                defined in the automation will be run.
              </p>
              <p className="text-sm text-gray-600" data-id="element-821">
                Are you sure you want to continue?
              </p>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end" data-id="element-822">
              <button onClick={() => setShowWarningModal(false)} className="px-4 py-2 border border-gray-200 rounded-md mr-2 text-sm font-medium" data-id="element-823">
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium" onClick={handleRunTest} data-id="element-824">
                Run Automation
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
// TypeBadge component for consistency
const TypeBadge = ({
  type
}) => {
  let bgColor = 'bg-gray-100';
  let textColor = 'text-gray-700';
  let borderColor = 'border-gray-200';
  let icon = <Zap className="w-3 h-3 mr-1" data-id="element-825" />;
  if (type === 'Builder') {
    icon = <Layers className="w-3 h-3 mr-1" data-id="element-826" />;
  }
  return <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${bgColor} ${textColor} ${borderColor}`} data-id="element-827">
      {icon} {type}
    </span>;
};